/**
 * Authentication Interceptor
 *
 * Functional HTTP interceptor that automatically adds the JWT authentication token
 * to the Authorization header of outgoing HTTP requests.
 *
 * This interceptor follows the Angular 15+ functional interceptor pattern.
 *
 * @example
 * ```typescript
 * // In app.config.ts
 * provideHttpClient(
 *   withInterceptors([authInterceptor, errorInterceptor, loadingInterceptor])
 * )
 * ```
 */

import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { TokenService } from '../services/token.service';

/**
 * Intercepts HTTP requests and adds the Bearer token to the Authorization header
 *
 * If a valid token exists in TokenService, it will be added to the request headers.
 * Requests without a token will proceed unchanged.
 *
 * @param req - The outgoing HTTP request
 * @param next - The next handler in the interceptor chain
 * @returns Observable of the HTTP response
 *
 * @example
 * Request before interceptor:
 * ```
 * GET /api/company-dashboard/schools
 * ```
 *
 * Request after interceptor:
 * ```
 * GET /api/company-dashboard/schools
 * Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...
 * ```
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);
  const token = tokenService.getToken();

  // If no token exists, proceed with the original request
  if (!token) {
    return next(req);
  }

  // Clone the request and add the Authorization header
  const authReq = req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });

  return next(authReq);
};
